import { toUtcDateKey } from '@/utils/date';
import { formatUtcDateTime } from '@/utils/format';
import { omit, trimStringsDeep } from '@/utils/object';
import {
  type DowntimeDay,
  type DowntimeRuleInput,
  downtimeRuleSchema,
  isPerpetual,
  type MarketMakerInput,
  type ProgramInput,
  type ProgramType,
  RULE_COMPONENTS,
  type RuleComponent,
  SESSIONS,
} from './mm-program-schema';

const DEFAULT_THRESHOLD = 90;
const DEFAULT_EXPONENT_START = 0;
const DEFAULT_EXPONENT = 7;
const MS_PER_HOUR = 60 * 60 * 1000;

export const SESSION_LABELS: Record<string, string> = { C: 'Core', E: 'Extended', W: 'Weekend' };
export const COMPONENT_LABELS: Record<RuleComponent, string> = { rest: 'Rest', linger: 'Linger', skew: 'Skew', vol: 'Volatility', oob: 'Out of book' };

export const PROGRAM_TYPE_LABELS: Record<ProgramType, string> = {
  least_performing_bucket: 'Least performing bucket',
  independent_buckets: 'Independent buckets',
};
export const DOWNTIME_DAY_LABELS: Record<DowntimeDay, string> = { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' };
export const INSTRUMENT_PATTERN = /^[A-Z0-9_]+$/;

export const toIsoDate = (day: string) => (day ? `${day}T00:00:00.000000Z` : '');

export const todayIsoDate = (now = new Date()) => toIsoDate(toUtcDateKey(now));

export const isOpen = (program: ProgramInput) => !program.end_date.trim();

export const compensationOf = (program: ProgramInput) => (program.type === 'independent_buckets' ? program.b1.compensation + program.b2.compensation : program.compensation);

export const dayLabel = (value: string) => (value.trim() ? value.slice(0, 10) : 'Open');

export const endedByToday = (program: ProgramInput, now = new Date()) => !isOpen(program) && program.end_date.slice(0, 10) <= toUtcDateKey(now);

/**
 * A schedule whose exclusive end is before today: its days are scored, so a change to it applies once they are rescored.
 * One ending today is told apart, as a same-day cut-over is usually still being made.
 */
export const isScored = (program: ProgramInput, now = new Date()) => !isOpen(program) && program.end_date.slice(0, 10) < toUtcDateKey(now);

export type ScheduleStatus = 'scored' | 'endedToday' | 'inForce' | 'upcoming';

export interface ScheduleBar {
  /** Positions in the instrument's list: one schedule, or its C/E/W set */
  indices: number[];
  /** Calendar day */
  start: string;
  /** Exclusive calendar day; null while open */
  end: string | null;
  sessioned: boolean;
  status: ScheduleStatus;
  /** Timeline row; only schedules that do not overlap share one */
  lane: number;
}

export const scheduleStatus = (program: ProgramInput, now: Date): ScheduleStatus => {
  if (isScored(program, now)) return 'scored';
  if (endedByToday(program, now)) return 'endedToday';
  return program.start_date.slice(0, 10) > toUtcDateKey(now) ? 'upcoming' : 'inForce';
};

/** An instrument's schedules for a timeline, a C/E/W set drawn once, ordered by start. */
export const scheduleBars = (programs: ProgramInput[], now = new Date()): ScheduleBar[] => {
  const ordered = programs
    .flatMap((_, index) => {
      const indices = sessionGroupIndices(programs, index);
      return indices[0] === index ? [{ indices, program: programs[index] }] : [];
    })
    .sort((left, right) => left.program.start_date.localeCompare(right.program.start_date));
  const bars: ScheduleBar[] = [];
  for (const { indices, program } of ordered) {
    const start = program.start_date.slice(0, 10);
    const isBusy = (lane: number) => bars.some((bar) => bar.lane === lane && (bar.end === null || bar.end > start));
    let lane = 0;
    while (isBusy(lane)) lane += 1;
    const end = isOpen(program) ? null : program.end_date.slice(0, 10);
    bars.push({ indices, start, end, sessioned: program.session !== undefined, status: scheduleStatus(program, now), lane });
  }
  return bars;
};

export type RulesInput = NonNullable<ProgramInput['rules']>;
export type VolatilityInput = NonNullable<RulesInput['volatility']>;
export type OutOfBookInput = NonNullable<RulesInput['out_of_book']>;
/** What a volatility or out-of-book block starts from when it is first turned on */
export const NEW_VOLATILITY: VolatilityInput = { rungs_bps: [], add_bps: [], cooldown_s: 60, provider: '', instrument: '' };
export const NEW_OUT_OF_BOOK: OutOfBookInput = { min_gap_s: 10, full_s: 300, max_cut_fraction: 0.5 };
/** Rules a new perpetual program starts with, live in production */
const NEW_RULES = { place_rest_ms: 50, move_rest_ms: 50, fill_linger_ms: 100 } satisfies Partial<RulesInput>;
/**
 * The volatility ladder a new perpetual starts with, by base asset: the first rung is the Stressed regime and the second
 * Extreme, each adding its width to both B1 and B2. Below the first rung the SLA's own widths apply.
 */
const NEW_VOLATILITY_LADDERS: { bases: string[]; rungs_bps: number[]; add_bps: number[] }[] = [
  { bases: ['BTC', 'ETH', 'SOL', 'XRP'], rungs_bps: [10, 20], add_bps: [10, 20] },
  { bases: ['ADA', 'DOGE', 'LINK', 'SUI', 'LTC', 'TAO', 'SPCX'], rungs_bps: [15, 30], add_bps: [10, 20] },
  { bases: ['ONEX100', 'ONEX500'], rungs_bps: [5, 10], add_bps: [5, 10] },
];

/** An index price feed, as bop-api lists the ones that price each exchange instrument */
export interface IndexFeed {
  provider: string;
  symbol: string;
}

const newRules = (instrument: string, feed: IndexFeed | undefined): RulesInput => {
  const base = instrument.trim().toUpperCase().split('_')[0];
  const ladder = NEW_VOLATILITY_LADDERS.find((item) => item.bases.includes(base));
  // Without a known feed the ladder's provider and symbol are left blank, to be filled in.
  const volatility = ladder && {
    ...NEW_VOLATILITY,
    rungs_bps: [...ladder.rungs_bps],
    add_bps: [...ladder.add_bps],
    provider: feed?.provider ?? '',
    instrument: feed?.symbol ?? '',
  };
  return {
    live: volatility ? ['rest', 'linger', 'vol', 'oob'] : ['rest', 'linger', 'oob'],
    ...NEW_RULES,
    ...(volatility ? { volatility } : {}),
    out_of_book: { ...NEW_OUT_OF_BOOK },
  };
};

/**
 * A new instrument's first schedule, paying on the default exponent curve. A perpetual also gets the default rest, linger
 * and out-of-book rules, and the volatility ladder for its base asset measured on `feed`, all live. A follow-on schedule
 * copies the one before it instead (see `appendNextSchedule`).
 */
export const blankProgram = (start: string, instrument: string, feed?: IndexFeed): ProgramInput => ({
  type: 'least_performing_bucket',
  b1: { bps: 0, kpi: 0, nbbo: false },
  b2: { bps: 0, kpi: 0, nbbo: false },
  threshold: DEFAULT_THRESHOLD,
  compensation: 0,
  exponent_start: DEFAULT_EXPONENT_START,
  exponent: DEFAULT_EXPONENT,
  kpi_currency: 'USD',
  compensation_currency: 'USD',
  start_date: start,
  end_date: '',
  ...(isPerpetual(instrument) ? { rules: newRules(instrument, feed) } : {}),
});

/** Each instrument's own index feed; where several price one instrument, the first listed is used. */
export const feedsByInstrument = (sources: (IndexFeed & { instruments: string[] })[]): Map<string, IndexFeed> => {
  const feeds = new Map<string, IndexFeed>();
  for (const { provider, symbol, instruments } of sources) {
    for (const instrument of instruments) if (!feeds.has(instrument)) feeds.set(instrument, { provider, symbol });
  }
  return feeds;
};

/** Applies a rules edit; rules left with nothing but an empty live list are removed. */
export const patchRules = (program: ProgramInput, update: (rules: RulesInput) => RulesInput): ProgramInput => {
  const next = update(program.rules ?? { live: [] });
  const isEmpty = Object.entries(next).every(([field, value]) => value === undefined || (field === 'live' && Array.isArray(value) && value.length === 0));
  return isEmpty ? omit(program, 'rules') : { ...program, rules: next };
};

export const setRuleField = <K extends Exclude<keyof RulesInput, 'live'>>(rules: RulesInput, field: K, value: RulesInput[K] | undefined): RulesInput =>
  value === undefined ? (omit(rules, field) as RulesInput) : { ...rules, [field]: value };

/** Turns a component on or off in production, keeping the live list in component order. */
export const setRuleLive = (program: ProgramInput, component: RuleComponent, enabled: boolean): ProgramInput =>
  patchRules(program, (current) => ({ ...current, live: RULE_COMPONENTS.filter((item) => (item === component ? enabled : current.live.includes(item))) }));

/** A new downtime rule: a recurring 02:00–03:00 UTC Monday window, or a one-hour one-off from the next whole UTC hour. */
export const blankDowntimeRule = (type: DowntimeRuleInput['type'], now = new Date()): DowntimeRuleInput => {
  const base = { id: crypto.randomUUID(), enabled: true, reason: '' };
  if (type === 'recurring') return { ...base, type, days: ['MON'], start_time: '02:00', end_time: '03:00' };
  const start = (Math.floor(now.getTime() / MS_PER_HOUR) + 1) * MS_PER_HOUR;
  return { ...base, type, start: new Date(start).toISOString(), end: new Date(start + MS_PER_HOUR).toISOString() };
};

/** Moves a one-off window to `start`, keeping its length, so moving it into the past does not stretch it up to its old end. */
export const moveOneOffStart = <R extends { start: string; end: string }>(rule: R, start: string): R => {
  const length = Date.parse(rule.end) - Date.parse(rule.start);
  const to = Date.parse(start);
  return Number.isFinite(length) && length > 0 && Number.isFinite(to) ? { ...rule, start, end: new Date(to + length).toISOString() } : { ...rule, start };
};

/**
 * Whether a rule covers time before now: a one-off that has started, or a recurring rule with no first day or one
 * before tomorrow. Runs in that time may be scored already, so they take the rule once they are rescored.
 */
export const hasDowntimeStarted = (rule: DowntimeRuleInput, now = new Date()) =>
  rule.type === 'one_off' ? Date.parse(rule.start) <= now.getTime() : !rule.start_date || rule.start_date <= toUtcDateKey(now);

/** The first problem on each top-level field of a rule. */
export const findDowntimeRuleProblems = (rule: DowntimeRuleInput): Partial<Record<string, string>> => {
  const result = downtimeRuleSchema.safeParse(trimStringsDeep(rule));
  const byField = new Map<string, string>();
  for (const issue of result.success ? [] : result.error.issues) {
    const field = String(issue.path[0] ?? '');
    if (!byField.has(field)) byField.set(field, issue.message);
  }
  return Object.fromEntries(byField);
};

/** A recurring rule's days in force: " · 2026-10-01 → 2026-12-01", " · from 2026-10-01", " · until 2026-12-01", or nothing. */
const recurringSpan = ({ start_date: from, end_date: until }: { start_date?: string; end_date?: string }) => {
  if (from && until) return ` · ${from} → ${until}`;
  if (from) return ` · from ${from}`;
  return until ? ` · until ${until}` : '';
};

/** "Mon, Thu 02:00–03:30 UTC · from 2026-10-01" or "2026-10-20 08:00 UTC → 2026-10-20 12:00 UTC". */
export const describeDowntimeRule = (rule: DowntimeRuleInput) =>
  rule.type === 'recurring'
    ? `${rule.days.map((day) => DOWNTIME_DAY_LABELS[day]).join(', ')} ${rule.start_time}–${rule.end_time} UTC${recurringSpan(rule)}`
    : `${formatUtcDateTime(rule.start)} → ${formatUtcDateTime(rule.end)}`;

/** Replaces the schedule at `index` with a Core, Extended and Weekend set carrying its terms, at the same position. */
export const splitIntoSessions = (programs: ProgramInput[], index: number): ProgramInput[] =>
  programs.flatMap((program, position) => (position === index ? SESSIONS.map((session) => ({ ...program, session })) : [program]));

/**
 * Replaces the session set at `indices` with one schedule for all hours. A set must share every term except bucket widths
 * and sizes, so only Core's buckets are kept. Returns the list and the merged schedule's index.
 */
export const mergeSessions = (programs: ProgramInput[], indices: number[]): { programs: ProgramInput[]; index: number } => {
  const first = Math.min(...indices);
  const core = indices.map((index) => programs[index]).find((program) => program.session === 'C') ?? programs[first];
  const merged = omit(core, 'session') as ProgramInput;
  return {
    programs: programs.flatMap((program, position) => (position === first ? [merged] : indices.includes(position) ? [] : [program])),
    index: programs.slice(0, first).length,
  };
};

/** Indices of the programs edited together with `index`: its C/E/W session set, or just itself. */
export const sessionGroupIndices = (programs: ProgramInput[], index: number): number[] => {
  const program = programs[index];
  if (!program) return [];
  if (!program.session) return [index];
  return programs.flatMap((item, itemIndex) => (item.session && item.start_date === program.start_date && item.end_date === program.end_date ? [itemIndex] : []));
};

export const orderBySession = (indices: number[], programs: ProgramInput[]) =>
  [...indices].sort((left, right) => SESSIONS.indexOf(programs[left].session ?? 'C') - SESSIONS.indexOf(programs[right].session ?? 'C'));

/** Converts between variants, carrying pay terms across (top-level for least performing, per bucket for independent). */
export const setProgramType = (program: ProgramInput, type: ProgramType): ProgramInput => {
  if (program.type === type) return program;
  const { b1, b2, threshold, compensation, exponent_start, exponent, ...common } = program;
  const bucketBasics = (bucket: typeof b1) => ({ bps: bucket.bps, kpi: bucket.kpi, nbbo: bucket.nbbo });
  if (program.type === 'least_performing_bucket') {
    const pay = {
      threshold: threshold ?? DEFAULT_THRESHOLD,
      compensation: compensation ?? 0,
      exponent_start: exponent_start ?? DEFAULT_EXPONENT_START,
      exponent: exponent ?? DEFAULT_EXPONENT,
    };
    return { ...common, type: 'independent_buckets', b1: { ...bucketBasics(b1), ...pay }, b2: { ...bucketBasics(b2), ...pay } };
  }
  const pay = program.b1;
  return {
    ...common,
    type: 'least_performing_bucket',
    b1: bucketBasics(b1),
    b2: bucketBasics(b2),
    threshold: pay.threshold,
    compensation: pay.compensation,
    ...(pay.exponent_start != null && pay.exponent != null ? { exponent_start: pay.exponent_start, exponent: pay.exponent } : {}),
  };
};

type NextScheduleResult = { programs: ProgramInput[]; index: number } | { error: string };

/** Ends every open schedule at `start` and opens copies of them from `start`; with none open, copies the latest set. */
export const appendNextSchedule = (programs: ProgramInput[], start: string): NextScheduleResult => {
  if (!start) return { error: 'Choose a start date.' };
  const open = programs.filter(isOpen);
  const latestStart = programs
    .map((program) => program.start_date)
    .sort()
    .at(-1);
  const latest = programs.findIndex((program) => program.start_date === latestStart);
  const seeds = open.length ? open : sessionGroupIndices(programs, latest).map((index) => programs[index]);
  if (!seeds.length) return { error: 'This instrument has no program to copy.' };
  if (seeds.some((program) => program.start_date.slice(0, 10) >= start.slice(0, 10))) return { error: 'The new start must be after the schedule it replaces.' };
  const ended = programs.map((program) => (isOpen(program) ? { ...program, end_date: start } : program));
  const copies = seeds.map((program) => ({ ...structuredClone(program), start_date: start, end_date: '' }));
  return { programs: [...ended, ...copies], index: ended.length };
};

/** The loaded document as the form edits it: retired fields the job no longer reads are dropped and show up in the review. */
export const toDraft = (marketMaker: MarketMakerInput): MarketMakerInput => {
  const draft = structuredClone(marketMaker);
  for (const programs of Object.values(draft.programs ?? {})) {
    for (const program of programs) delete (program as { notional?: unknown }).notional;
  }
  return draft;
};

/** Market makers as picker options, by name, with the inactive ones marked. */
export const marketMakerOptions = (marketMakers: { pid: string; name: string; active?: boolean }[]) =>
  marketMakers
    .map(({ pid, name, active }) => ({ value: pid, label: name, detail: active === true ? undefined : 'Inactive' }))
    .sort((left, right) => left.label.localeCompare(right.label));

/** Rung lists are typed as `10;15;20`. */
export const parseNumberList = (text: string): number[] =>
  text
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map(Number);

export const formatNumberList = (values: number[]) => values.join(';');
